/**
 * GDPR consent management for analytics (Google Analytics 4 + PostHog).
 *
 * State machine
 * -------------
 * Stored in localStorage under `analytics_consent`:
 *   "granted"  → user clicked Accept; GA + PostHog active
 *   "denied"   → user clicked Reject; both stay opted-out
 *   null       → first visit; show banner, both kept opted-out
 *
 * Why opt-out by default
 * ----------------------
 * Italian/EU users require explicit consent BEFORE non-essential cookies fire
 * (GDPR + Italian Garante della Privacy guidelines, May 2021). We default to
 * denied via Google's Consent Mode v2 (declared in index.html) and we init
 * PostHog with `opt_out_capturing_by_default: true`.
 */

const KEY = "analytics_consent";

export function getConsent() {
  try {
    return localStorage.getItem(KEY); // "granted" | "denied" | null
  } catch {
    return null;
  }
}

export function setConsent(state /* "granted" | "denied" */) {
  try {
    localStorage.setItem(KEY, state);
  } catch {
    /* ignore quota / private-mode errors */
  }
  applyConsent(state);
  // Notify subscribers (banner can hide; rest of app can react).
  window.dispatchEvent(new CustomEvent("consent-changed", { detail: state }));
}

/**
 * Push the consent state to GA4 Consent Mode and PostHog.
 *
 * Safe to call multiple times — both libraries are idempotent on
 * consent toggling.
 */
export function applyConsent(state) {
  if (typeof window === "undefined") return;
  const granted = state === "granted";

  // GA4 Consent Mode v2
  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: granted ? "granted" : "denied",
      ad_storage: granted ? "granted" : "denied",
      ad_user_data: granted ? "granted" : "denied",
      ad_personalization: granted ? "granted" : "denied",
    });
  }

  // PostHog opt-in / opt-out
  const ph = window.posthog;
  if (ph && typeof ph.opt_in_capturing === "function") {
    if (granted) {
      try { ph.opt_in_capturing(); } catch { /* no-op */ }
    } else {
      try { ph.opt_out_capturing(); } catch { /* no-op */ }
    }
  }
}

/**
 * Apply persisted consent on app boot. Called once from App.js.
 * If no choice was made yet, leaves analytics opted-out (banner will show).
 */
export function bootstrapConsent() {
  const saved = getConsent();
  if (saved === "granted" || saved === "denied") {
    applyConsent(saved);
  } else {
    applyConsent("denied"); // explicit denial until user chooses
  }
}
